import { useCopilotChatInternal } from '@copilotkit/react-core';
import { useChatContext } from '@copilotkit/react-ui';
import classNames from 'classnames';
import React, { Fragment, useMemo } from 'react';
import { formatByteAdaptive } from '@/infrastructure/utils/digit';
import { useAgentContext } from './agent';
import inputStyles from './copilot-input.module.scss';
import FileIcon from './icons/file-icon.svg?react';
import styles from './index.module.scss';

// 文件消息接口
interface FileMessage {
  type: 'file';
  files: Array<{
    id: string;
    file: any;
    uploadStatus: 'pending' | 'uploading' | 'success' | 'error';
    uploadProgress?: number;
    uploadUrl?: string;
    errorMessage?: string;
    fileSize?: number; // 文件大小（字节）
    fileName?: string; // 文件名
  }>;
  textMessage?: any;
}

// 判断消息是否为文件类型
const isFileMessage = (content: string): FileMessage | null => {
  try {
    const parsed = JSON.parse(content);
    if (parsed.type === 'file' && Array.isArray(parsed.files)) {
      return parsed;
    }
  } catch (e) {
    // 不是有效的 JSON 或不是文件消息
  }
  return null;
};

// 从 URL 中提取文件名
const getFileNameFromUrl = (url: string): string => {
  try {
    const parts = url.split('/');
    return decodeURIComponent(parts[parts.length - 1]);
  } catch {
    return 'unknown';
  }
};

// 只读文件项组件（用于显示已上传的文件）
const ReadOnlyFileItem: React.FC<{ fileData: FileMessage['files'][0] }> = ({ fileData }) => {
  const fileName = fileData.fileName || getFileNameFromUrl(fileData.uploadUrl || '');
  const fileExtension = fileName.split('.').pop()
    ?.toLowerCase() || 'file';

  const getStatusDisplay = () => {
    switch (fileData.uploadStatus) {
      case 'success':
        // 如果有文件大小，显示文件类型和大小
        if (fileData.fileSize) {
          const formattedSize = formatByteAdaptive(fileData.fileSize);
          return `${fileExtension} | ${formattedSize.num}${formattedSize.unit}`;
        }
        return fileExtension;
      case 'error':
        return '上传失败';
      default:
        return fileExtension;
    }
  };

  return (
    <div className={inputStyles.fileItem}>
      <div><FileIcon /></div>
      <div className={inputStyles.fileContent}>
        <div className={inputStyles.fileName}>{fileName}</div>
        <div className={classNames(inputStyles.fileSize, {
          [inputStyles.success]: fileData.uploadStatus === 'success',
          [inputStyles.error]: fileData.uploadStatus === 'error',
        })}>
          {getStatusDisplay()}
        </div>
      </div>
    </div>
  );
};

// Tool 消息组件
export const CustomUserMessage: React.FC<any> = (props: any) => {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { inProgress, isCurrentMessage, DefaultUserMessage, message, ...restProps } = props;
  const { icons } = useChatContext();
  const LoadingIcon = () => <span>{icons.activityIcon}</span>;
  const { isWaiting } = useAgentContext();
  const { messages } = useCopilotChatInternal();

  const isLastUserMessage = useMemo(() => {
    const userMessages = messages.filter(m => m.role === 'user');
    return userMessages[userMessages.length - 1].id === message.id;
  }, [messages, message.id]);

  // 检查是否为文件消息
  const fileMessage = useMemo(() => {
    if (typeof message.content === 'string') {
      return isFileMessage(message.content);
    }
    return null;
  }, [message.content]);

  // 如果是文件消息，渲染文件列表
  if (fileMessage) {
    return (
      <Fragment>
        <div className={styles.userFileMessage}>
          <div className={classNames(inputStyles.fileList, styles.userFileList)}>
            {fileMessage.files.map(fileData => (
              <ReadOnlyFileItem
                key={fileData.id}
                fileData={fileData}
              />
            ))}
          </div>
          {fileMessage.textMessage.content && <DefaultUserMessage {...restProps} message={fileMessage.textMessage} />}
        </div>
        {((isLastUserMessage && inProgress && isCurrentMessage)) && <LoadingIcon />}
      </Fragment>
    );
  }

  // 普通文本消息
  return (
    <Fragment>
      <DefaultUserMessage {...restProps} message={message} />
      {(isLastUserMessage && isWaiting) && <LoadingIcon />}
    </Fragment>
  );
};
